import React from "react";
import { Link } from "react-router-dom";
import {
  Radar, ChevronDown, Stethoscope, Home as HomeIcon, Building2, UtensilsCrossed,
  ShoppingBag, Car, Wrench, Dumbbell, Briefcase, MapPin, Footprints, BarChart3, Clapperboard, Gem,
} from "lucide-react";
import "../../pages/smartreach/marketing.css";
import AskSusan from "./AskSusan";

export const INDUSTRY_LINKS = [
  { slug: "doctors", label: "Medical & Dental", d: "HIPAA + state-law compliant growth", icon: Stethoscope },
  { slug: "medical-professionals", label: "Medical Professionals", d: "Reach doctors, nurses & healthcare staff (B2B)", icon: Stethoscope },
  { slug: "real-estate", label: "Real Estate", d: "Farm-area targeting & listing attribution", icon: HomeIcon },
  { slug: "commercial-real-estate", label: "Commercial Real Estate", d: "Lease & sell space faster", icon: Building2 },
  { slug: "apartments", label: "Apartments & Multifamily", d: "Fill units and cut vacancy days", icon: Building2 },
  { slug: "property-management", label: "Property Management", d: "Portfolio-wide leasing & retention", icon: HomeIcon },
  { slug: "restaurants", label: "Restaurants & QSR", d: "Fill tables and grow repeat diners", icon: UtensilsCrossed },
  { slug: "retail", label: "Retail & Grocery", d: "Turn nearby shoppers into visits", icon: ShoppingBag },
  { slug: "auto", label: "Auto Dealerships", d: "More test drives and service visits", icon: Car },
  { slug: "home-services", label: "Home Services", d: "Qualified, booked jobs", icon: Wrench },
  { slug: "entertainment-events", label: "Gyms & Fitness", d: "Trials and new memberships", icon: Dumbbell },
  { slug: "professional-services", label: "Professional Services", d: "Higher-value local clients", icon: Briefcase },
  { slug: "luxury", label: "Luxury Products", d: "Reach affluent & high-net-worth buyers", icon: Gem },
];

export const SrLogo = ({ light }) => {
  let wl = null;
  try { wl = JSON.parse(sessionStorage.getItem("wl_brand") || "null"); } catch { wl = null; }
  if (wl && (wl.logo_url || wl.name)) {
    return (
      <Link to="/" className="srx-logo" data-testid="sr-home-logo">
        {wl.logo_url
          ? <img src={wl.logo_url} alt={wl.name || "logo"} style={{ height: 26, maxWidth: 160, objectFit: "contain" }} />
          : <><span className="srx-logo-mark" style={wl.accent_color ? { background: wl.accent_color } : {}}><Radar size={18} /></span>{wl.name}</>}
      </Link>
    );
  }
  return (
    <Link to="/" className="srx-logo" data-testid="sr-home-logo" style={light ? {} : {}}>
      <span className="srx-logo-mark"><Radar size={18} /></span>
      SmartReach<em>AI</em>
    </Link>
  );
};

const NavMenu = ({ label, testid, items, cols = 2 }) => (
  <div className="srx-navitem" data-testid={testid}>
    <button className="srx-navtrigger" data-testid={`${testid}-trigger`} aria-haspopup="true">
      {label} <ChevronDown size={15} className="srx-caret" />
    </button>
    <div className={`srx-dropdown ${cols === 1 ? "one-col" : ""}`} role="menu" data-testid={`${testid}-menu`}>
      {items.map((i) => (
        <Link key={i.to} to={i.to} className="srx-dropitem" role="menuitem" data-testid={i.testid}>
          <i.icon size={18} className="srx-dropico" />
          <span style={{ margin: 0 }}><b>{i.label}</b><span>{i.d}</span></span>
        </Link>
      ))}
    </div>
  </div>
);

const PRODUCT_LINKS = [
  { to: "/competitor-targeting", label: "Competitor Targeting", d: "Win customers away from rivals", icon: Radar, testid: "nav-product-competitor-targeting" },
  { to: "/visit-intelligence", label: "Visit Intelligence", d: "Turn attention into store visits", icon: MapPin, testid: "nav-product-visit-intelligence" },
  { to: "/footfall-attribution", label: "Footfall Attribution", d: "Prove real in-store visits", icon: Footprints, testid: "nav-product-footfall-attribution" },
  { to: "/solutions/movie-marketing", label: "Movie Marketing Studio", d: "Market film releases by market", icon: Clapperboard, testid: "nav-product-movie-marketing" },
  { to: "/comparison", label: "Compare", d: "See how SmartReachAI stacks up", icon: BarChart3, testid: "nav-product-compare" },
];

const INDUSTRY_NAV = INDUSTRY_LINKS.map((i) => ({ to: `/solutions/${i.slug}`, label: i.label, d: i.d, icon: i.icon, testid: `nav-industry-${i.slug}` }));

export const SrHeader = () => (
  <nav className="srx-nav">
    <div className="srx-wrap srx-nav-inner">
      <SrLogo />
      <div className="srx-navlinks">
        <Link to="/how-it-works" className="srx-link" data-testid="sr-nav-how-it-works">How it works</Link>
        <NavMenu label="Product" testid="nav-product" items={PRODUCT_LINKS} cols={1} />
        <NavMenu label="Industries" testid="nav-industries" items={INDUSTRY_NAV} cols={2} />
        <Link to="/pricing" className="srx-link" data-testid="sr-nav-pricing">Pricing</Link>
      </div>
      <div className="srx-nav-actions">
        <Link to="/login" className="srx-nav-login" data-testid="sr-login-btn">Log in</Link>
        <Link to="/signup" className="srx-navcta" data-testid="sr-signup-btn">GET STARTED</Link>
      </div>
    </div>
  </nav>
);

export const SrFooter = () => (
  <footer className="srx-footer">
    <div className="srx-wrap srx-footer-grid">
      <div>
        <SrLogo />
        <p className="srx-foot-tag">Real foot-traffic attribution that turns competitor visits into measured customers at your door — proof Google &amp; Meta can&apos;t give a local business.</p>
      </div>
      <div>
        <h4>Product</h4>
        <Link className="srx-link" to="/how-it-works">How it works</Link>
        <Link className="srx-link" to="/competitor-targeting">Competitor Targeting</Link>
        <Link className="srx-link" to="/visit-intelligence">Visit Intelligence</Link>
        <Link className="srx-link" to="/footfall-attribution">Footfall Attribution</Link>
        <Link className="srx-link" to="/radio-digital">Radio + Digital</Link>
      </div>
      <div>
        <h4>Solutions</h4>
        <Link className="srx-link" to="/solutions/restaurants">Restaurants</Link>
        <Link className="srx-link" to="/solutions/retail">Retail</Link>
        <Link className="srx-link" to="/solutions/auto">Auto</Link>
        <Link className="srx-link" to="/solutions/movie-marketing">Movie Marketing</Link>
        <Link className="srx-link" to="/professional-service">Professional Service</Link>
      </div>
      <div>
        <h4>Company</h4>
        <Link className="srx-link" to="/pricing">Pricing</Link>
        <Link className="srx-link" to="/comparison">Comparison</Link>
        <Link className="srx-link" to="/privacy">Privacy</Link>
        <Link className="srx-link" to="/terms">Terms</Link>
      </div>
    </div>
    <div className="srx-wrap srx-foot-legal">
      <span>SMARTREACHAI — REAL FOOT-TRAFFIC ATTRIBUTION FOR LOCAL BUSINESS</span>
      <span>© {new Date().getFullYear()} SMARTREACHAI · BY FUNASIA DIGITAL</span>
    </div>
  </footer>
);

export const MarketingPage = ({ children }) => (
  <div className="sr-root srx">
    <SrHeader />
    {children}
    <SrFooter />
    <AskSusan />
  </div>
);
